import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';

// ── Toast ─────────────────────────────────────────────────────────────────────
const TOAST_CFG = {
  error:   {bg:'#FFF0EF',bdr:'#FFD4D1',txt:'#C0392B',dot:'#FF3B30'},
  warning: {bg:'#FFF8E6',bdr:'#FFE9A0',txt:'#8B6914',dot:'#FF9500'},
  success: {bg:'#EDFAF2',bdr:'#B7EFD0',txt:'#1A6B3C',dot:'#34C759'},
};
const Toast = ({msg,tipo='success',onClose}) => {
  React.useEffect(()=>{ const t=setTimeout(onClose,3500); return()=>clearTimeout(t); },[onClose]);
  const cfg = TOAST_CFG[tipo]||TOAST_CFG.success;
  return (
    <Box sx={{position:'fixed',top:20,right:20,zIndex:99999,maxWidth:340,
      bgcolor:cfg.bg,borderRadius:'14px',p:1.8,border:`1px solid ${cfg.bdr}`,
      boxShadow:'0 8px 24px rgba(0,0,0,0.12)',display:'flex',alignItems:'flex-start',gap:1.2,
      animation:'slideIn 0.3s ease',
      '@keyframes slideIn':{'0%':{transform:'translateX(100%)',opacity:0},'100%':{transform:'translateX(0)',opacity:1}}}}>
      <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:cfg.dot,flexShrink:0,mt:0.5}}/>
      <Typography sx={{fontSize:'0.8rem',color:cfg.txt,lineHeight:1.5,flex:1}}>{msg}</Typography>
      <Box component="button" onClick={onClose}
        sx={{width:18,height:18,borderRadius:'50%',border:'none',bgcolor:'rgba(0,0,0,0.08)',
          cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={cfg.txt} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </Box>
    </Box>
  );
};

// ── Privilegios disponibles ───────────────────────────────────────────────────
const PRIVILEGIOS = ['Solo lectura','Lectura/Escritura','Total'];
const PRIV_COLOR = {
  'Solo lectura':     IOS.green,
  'Lectura/Escritura':IOS.orange,
  'Total':            IOS.red,
};

// ── Componentes base ──────────────────────────────────────────────────────────
const TH = ({children,right,center,w}) => (
  <th style={{padding:'9px 10px',width:w||'auto',
    textAlign:right?'right':center?'center':'left',
    fontSize:'0.6rem',fontWeight:700,color:IOS.label2,letterSpacing:'0.06em',
    textTransform:'uppercase',backgroundColor:IOS.surface,
    borderBottom:`1px solid ${IOS.separator}`,whiteSpace:'nowrap',
    position:'sticky',top:0,zIndex:1}}>{children}</th>
);

const Modal = ({open,onClose,children,w=480}) => {
  if(!open) return null;
  return (
    <Box sx={{position:'fixed',inset:0,zIndex:9999,bgcolor:'rgba(0,0,0,0.4)',
      display:'flex',alignItems:{xs:'flex-end',sm:'center'},justifyContent:'center',
      backdropFilter:'blur(4px)'}} onClick={onClose}>
      <Box onClick={e=>e.stopPropagation()}
        sx={{bgcolor:IOS.surface,borderRadius:{xs:'20px 20px 0 0',sm:'20px'},
          overflow:'hidden',width:{xs:'100%',sm:typeof w==='string'?w:w},
          mx:{xs:0,sm:2},maxHeight:{xs:'95vh',sm:'90vh'},
          display:'flex',flexDirection:'column',
          boxShadow:'0 24px 60px rgba(0,0,0,0.22)'}}>
        {children}
      </Box>
    </Box>
  );
};

const ModalHeader = ({title,subtitle,onClose,color=IOS.blue,icon}) => (
  <Box sx={{px:2.5,pt:2.5,pb:2,borderBottom:`1px solid ${IOS.separator}`,flexShrink:0,
    display:'flex',alignItems:'center',justifyContent:'space-between'}}>
    <Box sx={{display:'flex',alignItems:'center',gap:1.2}}>
      <Box sx={{width:34,height:34,borderRadius:'10px',bgcolor:`${color}15`,
        display:'flex',alignItems:'center',justifyContent:'center'}}>{icon}</Box>
      <Box>
        <Typography sx={{fontWeight:700,fontSize:'0.92rem',color:IOS.label}}>{title}</Typography>
        {subtitle&&<Typography sx={{fontSize:'0.7rem',color:IOS.label2}}>{subtitle}</Typography>}
      </Box>
    </Box>
    <Box component="button" onClick={onClose}
      sx={{width:28,height:28,borderRadius:'50%',border:'none',bgcolor:'#E5E5EA',cursor:'pointer',
        display:'flex',alignItems:'center',justifyContent:'center','&:hover':{bgcolor:'#D1D1D6'}}}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
    </Box>
  </Box>
);

const NIVELES = ['TOTAL','PACIAL'];
const TABS = [{id:'roles',label:'Roles'},{id:'permisos',label:'Permisos'}];

// ── Badge privilegio ──────────────────────────────────────────────────────────
const BadgePriv = ({priv}) => {
  const color = PRIV_COLOR[priv] || IOS.label2;
  return (
    <Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'20px',
      bgcolor:`${color}10`,border:`1px solid ${color}30`}}>
      <Typography sx={{fontSize:'0.58rem',fontWeight:700,color,whiteSpace:'nowrap'}}>{priv||'—'}</Typography>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
const Roles = () => {
  const navigate     = useNavigate();
  const user         = authService.getCurrentUser();
  const nf           = user?.nombreCompleto||user?.usuario||'';
  const handleLogout = ()=>{ authService.logout(); navigate('/login'); };

  const [tab,setTab]     = useState('roles');
  const [toast,setToast] = useState(null);
  const showToast = (msg,tipo='success') => setToast({msg,tipo});

  // ── ROLES ──────────────────────────────────────────────────────────────────
  const [roles,setRoles]         = useState([]);
  const [cargRoles,setCargRoles] = useState(true);
  const [busqRol,setBusqRol]     = useState('');
  const [rolSel,setRolSel]       = useState(null);

  const [modalRol,setModalRol]   = useState(false);
  const [editRol,setEditRol]     = useState(null);
  const [formRol,setFormRol]     = useState({desc:'',nivel:'TOTAL',tipo:'I'});
  const [guardRol,setGuardRol]   = useState(false);

  const [modalElimRol,setModalElimRol] = useState(false);
  const [elimRol,setElimRol]           = useState(null);
  const [eliminando,setEliminando]     = useState(false);

  const [permisosRol,setPermisosRol]   = useState([]);
  const [cargPermRol,setCargPermRol]   = useState(false);

  // Modal asignar — con privilegio
  const [modalAsignar,setModalAsignar]   = useState(false);
  const [todosPermisos,setTodosPermisos] = useState([]);
  const [busqPerm,setBusqPerm]           = useState('');
  const [asignando,setAsignando]         = useState(false);
  const [privSelAsignar,setPrivSelAsignar] = useState('Solo lectura');

  // Modificar privilegio inline
  const [modificandoPriv,setModificandoPriv] = useState(null); // idPermiso
  const [guardPriv,setGuardPriv]             = useState(false);

  const cargarRoles = useCallback(async () => {
    setCargRoles(true);
    try {
      const r = await api.get('/roles/listar',{params:{filtro:''}});
      if(r.data.success) setRoles(r.data.data||[]);
    } catch(e){console.error(e);} finally{setCargRoles(false);}
  },[]);

  useEffect(()=>{ cargarRoles(); },[cargarRoles]);

  const cargarPermisosRol = async (rol) => {
    setRolSel(rol); setPermisosRol([]); setCargPermRol(true);
    try {
      const r = await api.get('/roles/permisos-de-rol',{params:{idRol:rol.ROL_IDROL}});
      if(r.data.success) setPermisosRol(r.data.data||[]);
    } catch(e){console.error(e);} finally{setCargPermRol(false);}
  };

  const abrirModalRol = (rol=null) => {
    setEditRol(rol);
    setFormRol(rol?{desc:rol.ROL_DESCRIPCION||'',nivel:rol.ROL_NIVEL||'TOTAL',tipo:rol.ROL_TIPO||'I'}:{desc:'',nivel:'TOTAL',tipo:'I'});
    setModalRol(true);
  };

  const guardarRol = async () => {
    if(!formRol.desc.trim()) return;
    setGuardRol(true);
    try {
      const r = editRol
        ? await api.put('/roles/modificar',{idRol:editRol.ROL_IDROL,descripcion:formRol.desc,nivel:formRol.nivel,tipo:formRol.tipo})
        : await api.post('/roles/crear',{descripcion:formRol.desc,nivel:formRol.nivel,tipo:formRol.tipo});
      showToast(r.data.message, r.data.success?'success':'error');
      if(r.data.success){ setModalRol(false); cargarRoles(); }
    } catch(e){console.error(e);} finally{setGuardRol(false);}
  };

  const eliminarRol = async () => {
    if(!elimRol) return; setEliminando(true);
    try {
      const r = await api.delete(`/roles/eliminar/${elimRol.ROL_IDROL}`);
      showToast(r.data.message, r.data.success?'success':'error');
      if(r.data.success){ setModalElimRol(false); if(rolSel?.ROL_IDROL===elimRol.ROL_IDROL) setRolSel(null); cargarRoles(); }
    } catch(e){console.error(e);} finally{setEliminando(false);}
  };

  const quitarPermiso = async (perm) => {
    try {
      const r = await api.delete('/roles/quitar-permiso',{params:{idRol:rolSel.ROL_IDROL,idPermiso:perm.PERM_IDPERMISO||perm.RPERM_IDPERMISO}});
      showToast(r.data.message, r.data.success?'success':'error');
      if(r.data.success) cargarPermisosRol(rolSel);
    } catch(e){console.error(e);}
  };

  const modificarPrivilegio = async (perm, nuevoPriv) => {
    setGuardPriv(true);
    try {
      const r = await api.put('/roles/modificar-privilegio',{
        idRol: rolSel.ROL_IDROL,
        idPermiso: perm.PERM_IDPERMISO||perm.RPERM_IDPERMISO,
        privilegio: nuevoPriv,
      });
      showToast(r.data.message, r.data.success?'success':'error');
      if(r.data.success){ setModificandoPriv(null); cargarPermisosRol(rolSel); }
    } catch(e){console.error(e);} finally{setGuardPriv(false);}
  };

  const abrirAsignar = async () => {
    setModalAsignar(true); setBusqPerm(''); setPrivSelAsignar('Solo lectura');
    try {
      const r = await api.get('/roles/todos-permisos',{params:{filtro:''}});
      if(r.data.success) setTodosPermisos(r.data.data||[]);
    } catch(e){console.error(e);}
  };

  const asignarPermiso = async (perm) => {
    setAsignando(true);
    try {
      const r = await api.post('/roles/asignar-permiso',{
        idRol:rolSel.ROL_IDROL,
        idPermiso:perm.PERM_IDPERMISO,
        privilegio: privSelAsignar,
      });
      showToast(r.data.message, r.data.success?'success':'error');
      if(r.data.success){ cargarPermisosRol(rolSel); }
    } catch(e){console.error(e);} finally{setAsignando(false);}
  };

  // ── PERMISOS ───────────────────────────────────────────────────────────────
  const [permisos,setPermisos]       = useState([]);
  const [cargPerms,setCargPerms]     = useState(true);
  const [busqPermTab,setBusqPermTab] = useState('');
  const [modalPerm,setModalPerm]     = useState(false);
  const [editPerm,setEditPerm]       = useState(null);
  const [formPerm,setFormPerm]       = useState({desc:'',detalle:'',idModulo:null});
  const [guardPerm,setGuardPerm]     = useState(false);
  const [modalElimPerm,setModalElimPerm] = useState(false);
  const [elimPerm,setElimPerm]           = useState(null);
  const [eliminandoP,setEliminandoP]     = useState(false);

  const modulosPadre = useMemo(()=>permisos.filter(p=>!p.PERM_IDMODULO),[permisos]);

  const permisosRolAgrupados = useMemo(()=>{
    const grupos = {};
    permisosRol.forEach(p=>{
      const mod = p.MODULO||p.PERM_MODULO||'Principal';
      if(!grupos[mod]) grupos[mod]=[];
      grupos[mod].push(p);
    });
    return grupos;
  },[permisosRol]);

  const cargarPermisos = useCallback(async () => {
    setCargPerms(true);
    try {
      const r = await api.get('/roles/todos-permisos',{params:{filtro:''}});
      if(r.data.success) setPermisos(r.data.data||[]);
    } catch(e){console.error(e);} finally{setCargPerms(false);}
  },[]);

  useEffect(()=>{ if(tab==='permisos') cargarPermisos(); },[tab,cargarPermisos]);

  const guardarPerm = async () => {
    if(!formPerm.desc.trim()) return;
    setGuardPerm(true);
    try {
      const descFinal    = formPerm.desc.toLowerCase().trim();
      const detalleFinal = formPerm.detalle.trim()
        ? formPerm.detalle.trim().charAt(0).toUpperCase()+formPerm.detalle.trim().slice(1) : '';
      const r = editPerm
        ? await api.put('/roles/modificar-permiso',{idPermiso:editPerm.PERM_IDPERMISO,descripcion:descFinal,detalle:detalleFinal,idModulo:formPerm.idModulo})
        : await api.post('/roles/crear-permiso',{descripcion:descFinal,detalle:detalleFinal,idModulo:formPerm.idModulo});
      showToast(r.data.message, r.data.success?'success':'error');
      if(r.data.success){ setModalPerm(false); cargarPermisos(); }
    } catch(e){console.error(e);} finally{setGuardPerm(false);}
  };

  const eliminarPerm = async () => {
    if(!elimPerm) return; setEliminandoP(true);
    try {
      const r = await api.delete(`/roles/eliminar-permiso/${elimPerm.PERM_IDPERMISO}`);
      showToast(r.data.message, r.data.success?'success':'error');
      if(r.data.success){ setModalElimPerm(false); cargarPermisos(); }
    } catch(e){console.error(e);} finally{setEliminandoP(false);}
  };

  // Filtros
  const rolesFilt    = useMemo(()=>busqRol?roles.filter(r=>(r.ROL_DESCRIPCION||'').toLowerCase().includes(busqRol.toLowerCase())):roles,[roles,busqRol]);
  const permisosFilt = useMemo(()=>busqPermTab?permisos.filter(p=>(p.PERM_PERMISO_DESC||'').toLowerCase().includes(busqPermTab.toLowerCase())):permisos,[permisos,busqPermTab]);
  const permDisp     = useMemo(()=>{
    const ids = new Set(permisosRol.map(p=>p.PERM_IDPERMISO||p.RPERM_IDPERMISO));
    const filtrados = todosPermisos.filter(p=>!ids.has(p.PERM_IDPERMISO)&&
      (!busqPerm||(p.PERM_PERMISO_DESC||'').toLowerCase().includes(busqPerm.toLowerCase())||
                  (p.PERM_DEATALLE||'').toLowerCase().includes(busqPerm.toLowerCase())));
    const grupos = {};
    filtrados.forEach(p=>{
      const mod = p.MODULO||'MÓDULO PRINCIPAL';
      if(!grupos[mod]) grupos[mod]=[];
      grupos[mod].push(p);
    });
    return grupos;
  },[todosPermisos,permisosRol,busqPerm]);

  const tipoLabel = (t) => {
    if(t==='A') return {label:'Administrativo',color:IOS.orange};
    if(t==='U') return {label:'Ejecutora',color:IOS.blue};
    if(t==='I') return {label:'IPRESS',color:IOS.purple};
    return {label:t||'—',color:IOS.label2};
  };
  const nivelColor = (n) => {
    if(!n) return IOS.label2;
    if(n==='TOTAL') return IOS.red;
    if(n==='ADMINISTRATIVO') return IOS.orange;
    if(n==='OPERATIVO') return IOS.blue;
    if(n==='LECTURA') return IOS.green;
    return IOS.label2;
  };

  const Spinner = () => (
    <Box sx={{width:12,height:12,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.3)',
      borderTopColor:'white',animation:'spin 0.8s linear infinite',
      '@keyframes spin':{'0%':{transform:'rotate(0deg)'},'100%':{transform:'rotate(360deg)'}}}}/>
  );

  return (
    <>
      <Box sx={{minHeight:'100vh',bgcolor:IOS.bg}}>
        <HeaderIOS titulo="Roles y Permisos" subtitulo="Integra"
          onHome={()=>navigate('/dashboard')} onLogout={handleLogout}
          nombreCompleto={nf} avatarLetra={nf.charAt(0).toUpperCase()}/>

        <Box sx={{maxWidth:1400,mx:'auto',px:{xs:1.5,sm:2.6},pt:2.5,pb:6}}>

          {/* Tabs */}
          <Box sx={{display:'flex',gap:0.5,bgcolor:IOS.surface,borderRadius:'12px',p:0.5,
            width:'fit-content',boxShadow:'0 1px 4px rgba(0,0,0,0.07)',mb:2.5}}>
            {TABS.map(t=>(
              <Box key={t.id} onClick={()=>setTab(t.id)}
                sx={{px:2.5,py:0.8,borderRadius:'10px',cursor:'pointer',transition:'all 0.2s',
                  bgcolor:tab===t.id?IOS.bg:'transparent',
                  boxShadow:tab===t.id?'0 1px 3px rgba(0,0,0,0.1)':'none'}}>
                <Typography sx={{fontSize:'0.82rem',fontWeight:tab===t.id?700:500,
                  color:tab===t.id?IOS.label:IOS.label2}}>{t.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* ══ TAB ROLES ══ */}
          {tab==='roles'&&(
            <Box sx={{display:'flex',flexDirection:'column',gap:2.5}}>

              {/* Tabla roles */}
              <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',border:`1px solid ${IOS.separator}`,
                boxShadow:'0 1px 4px rgba(0,0,0,0.07)',overflow:'hidden'}}>

                <Box sx={{px:2.5,py:1.8,borderBottom:`1px solid ${IOS.separator}`,
                  display:'flex',alignItems:'center',gap:1.5,flexWrap:'wrap'}}>
                  <Box sx={{position:'relative',flex:1,minWidth:140}}>
                    <Box sx={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:IOS.label2,pointerEvents:'none'}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                    </Box>
                    <input placeholder="Buscar rol..." value={busqRol}
                      onChange={e=>setBusqRol(e.target.value)}
                      style={{width:'100%',height:32,paddingLeft:26,paddingRight:8,
                        border:`1px solid ${IOS.separator}`,borderRadius:8,fontSize:'0.78rem',
                        color:IOS.label,backgroundColor:IOS.bg,outline:'none',fontFamily:'inherit',boxSizing:'border-box'}}
                      onFocus={e=>e.target.style.borderColor=IOS.blue}
                      onBlur={e=>e.target.style.borderColor=IOS.separator}/>
                  </Box>
                  <Box sx={{px:1.2,py:0.3,bgcolor:IOS.bg,borderRadius:'20px',border:`1px solid ${IOS.separator}`}}>
                    <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.blue}}>{roles.length} roles</Typography>
                  </Box>
                  <Box component="button" onClick={()=>abrirModalRol()}
                    sx={{height:32,px:1.5,borderRadius:'10px',border:'none',bgcolor:IOS.blue,cursor:'pointer',
                      display:'flex',alignItems:'center',gap:0.6,'&:hover':{filter:'brightness(0.9)'}}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    <Typography sx={{fontSize:'0.78rem',fontWeight:700,color:'white'}}>Nuevo rol</Typography>
                  </Box>
                </Box>

                {cargRoles
                  ?<Box sx={{py:6,textAlign:'center'}}><CircularProgress size={24} sx={{color:IOS.blue}}/></Box>
                  :<>
                    {/* Desktop */}
                    <Box sx={{display:{xs:'none',md:'block'},overflowX:'auto',maxHeight:500,
                      '&::-webkit-scrollbar':{width:3},'&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4}}}>
                      <table style={{width:'100%',borderCollapse:'collapse'}}>
                        <thead><tr>
                          <TH>Rol</TH><TH>Nivel</TH><TH>Tipo</TH>
                          <TH center>Estado</TH><TH>Creado por</TH>
                          <TH center>Permisos</TH><TH center></TH>
                        </tr></thead>
                        <tbody>
                          {rolesFilt.length===0
                            ?<tr><td colSpan={7} style={{padding:'32px',textAlign:'center',color:IOS.label2,fontSize:'0.8rem'}}>Sin roles</td></tr>
                            :rolesFilt.map(rol=>{
                              const activo = rol.ROL_ESTADO===true||rol.ROL_ESTADO===1;
                              const sel    = rolSel?.ROL_IDROL===rol.ROL_IDROL;
                              return (
                                <tr key={rol.ROL_IDROL}
                                  style={{borderBottom:`1px solid ${IOS.separator}`,
                                    backgroundColor:sel?`${IOS.blue}06`:IOS.surface,
                                    transition:'background 0.1s',cursor:'pointer'}}
                                  onClick={()=>cargarPermisosRol(rol)}
                                  onMouseEnter={e=>{ if(!sel) e.currentTarget.style.backgroundColor=IOS.bg; }}
                                  onMouseLeave={e=>{ e.currentTarget.style.backgroundColor=sel?`${IOS.blue}06`:IOS.surface; }}>
                                  <td style={{padding:'9px 10px'}}>
                                    <Box sx={{display:'flex',alignItems:'center',gap:1}}>
                                      {sel&&<Box sx={{width:3,height:20,borderRadius:2,bgcolor:IOS.blue,flexShrink:0}}/>}
                                      <Typography sx={{fontSize:'0.78rem',fontWeight:sel?700:600,color:sel?IOS.blue:IOS.label}}>
                                        {rol.ROL_DESCRIPCION}
                                      </Typography>
                                    </Box>
                                  </td>
                                  <td style={{padding:'9px 10px'}}>
                                    <Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'20px',
                                      bgcolor:`${nivelColor(rol.ROL_NIVEL)}10`,border:`1px solid ${nivelColor(rol.ROL_NIVEL)}30`}}>
                                      <Typography sx={{fontSize:'0.62rem',fontWeight:700,color:nivelColor(rol.ROL_NIVEL)}}>
                                        {rol.ROL_NIVEL||'—'}
                                      </Typography>
                                    </Box>
                                  </td>
                                  <td style={{padding:'9px 10px'}}>
                                    {(()=>{const t=tipoLabel(rol.ROL_TIPO);return(
                                      <Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'20px',bgcolor:`${t.color}10`,border:`1px solid ${t.color}30`}}>
                                        <Typography sx={{fontSize:'0.62rem',fontWeight:700,color:t.color}}>{t.label}</Typography>
                                      </Box>
                                    );})()}
                                  </td>
                                  <td style={{padding:'9px 10px',textAlign:'center'}}>
                                    <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:activo?IOS.green:IOS.label3,mx:'auto',
                                      boxShadow:activo?`0 0 6px ${IOS.green}`:'none'}}/>
                                  </td>
                                  <td style={{padding:'9px 10px',fontSize:'0.7rem',color:IOS.label2}}>{rol.ROL_USUARIO_CREADOR||'—'}</td>
                                  <td style={{padding:'9px 10px',textAlign:'center'}}>
                                    <Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'20px',bgcolor:`${IOS.purple}10`,border:`1px solid ${IOS.purple}25`}}>
                                      <Typography sx={{fontSize:'0.62rem',fontWeight:700,color:IOS.purple}}>Ver →</Typography>
                                    </Box>
                                  </td>
                                  <td style={{padding:'6px 8px',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
                                    <Box sx={{display:'flex',gap:0.5,justifyContent:'center'}}>
                                      <Tooltip title="Editar" placement="top">
                                        <Box onClick={()=>abrirModalRol(rol)}
                                          sx={{width:26,height:26,borderRadius:'50%',bgcolor:`${IOS.blue}12`,display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer','&:hover':{bgcolor:`${IOS.blue}22`}}}>
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </Box>
                                      </Tooltip>
                                      <Tooltip title={activo?'Desactivar':'Inactivo'} placement="top">
                                        <Box onClick={activo?()=>{setElimRol(rol);setModalElimRol(true);}:undefined}
                                          sx={{width:26,height:26,borderRadius:'50%',bgcolor:'#FFF0EF',display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:activo?'pointer':'default',opacity:activo?1:0.3,'&:hover':{bgcolor:activo?'#FFD4D1':'#FFF0EF'}}}>
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                  </td>
                                </tr>
                              );
                            })
                          }
                        </tbody>
                      </table>
                    </Box>

                    {/* Mobile cards roles */}
                    <Box sx={{display:{xs:'flex',md:'none'},flexDirection:'column',p:1.5,gap:1}}>
                      {rolesFilt.length===0
                        ?<Typography sx={{textAlign:'center',color:IOS.label2,fontSize:'0.82rem',py:4}}>Sin roles</Typography>
                        :rolesFilt.map(rol=>{
                          const activo = rol.ROL_ESTADO===true||rol.ROL_ESTADO===1;
                          const sel    = rolSel?.ROL_IDROL===rol.ROL_IDROL;
                          const t      = tipoLabel(rol.ROL_TIPO);
                          return (
                            <Box key={rol.ROL_IDROL} onClick={()=>cargarPermisosRol(rol)}
                              sx={{p:1.5,borderRadius:'12px',border:`1.5px solid ${sel?IOS.blue:IOS.separator}`,
                                bgcolor:sel?`${IOS.blue}04`:IOS.bg,cursor:'pointer'}}>
                              <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',mb:1}}>
                                <Box sx={{flex:1,minWidth:0,mr:1}}>
                                  <Typography sx={{fontWeight:700,fontSize:'0.85rem',color:sel?IOS.blue:IOS.label}}>
                                    {rol.ROL_DESCRIPCION}
                                  </Typography>
                                  <Typography sx={{fontSize:'0.65rem',color:IOS.label2}}>{rol.ROL_USUARIO_CREADOR||'—'}</Typography>
                                </Box>
                                <Box sx={{display:'flex',alignItems:'center',gap:0.5}}>
                                  <Box sx={{width:7,height:7,borderRadius:'50%',bgcolor:activo?IOS.green:IOS.label3,
                                    boxShadow:activo?`0 0 5px ${IOS.green}`:'none'}}/>
                                </Box>
                              </Box>
                              <Box sx={{display:'flex',gap:0.6,flexWrap:'wrap',mb:1}}>
                                <Box sx={{px:0.8,py:0.2,borderRadius:'20px',bgcolor:`${nivelColor(rol.ROL_NIVEL)}10`,border:`1px solid ${nivelColor(rol.ROL_NIVEL)}30`}}>
                                  <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:nivelColor(rol.ROL_NIVEL)}}>{rol.ROL_NIVEL||'—'}</Typography>
                                </Box>
                                <Box sx={{px:0.8,py:0.2,borderRadius:'20px',bgcolor:`${t.color}10`,border:`1px solid ${t.color}30`}}>
                                  <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:t.color}}>{t.label}</Typography>
                                </Box>
                              </Box>
                              <Box sx={{display:'flex',gap:0.8}} onClick={e=>e.stopPropagation()}>
                                <Box component="button" onClick={()=>abrirModalRol(rol)}
                                  sx={{flex:1,height:30,borderRadius:'8px',border:`1px solid ${IOS.blue}30`,bgcolor:`${IOS.blue}08`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.5}}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.blue}}>Editar</Typography>
                                </Box>
                                {activo&&<Box component="button" onClick={()=>{setElimRol(rol);setModalElimRol(true);}}
                                  sx={{flex:1,height:30,borderRadius:'8px',border:'1px solid #FFD4D1',bgcolor:'#FFF0EF',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.5}}>
                                  <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.red}}>Desactivar</Typography>
                                </Box>}
                              </Box>
                            </Box>
                          );
                        })
                      }
                    </Box>
                  </>
                }
              </Box>

              {/* Panel permisos del rol */}
              {rolSel&&(
                <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',border:`1px solid ${IOS.separator}`,
                  boxShadow:'0 1px 4px rgba(0,0,0,0.07)',overflow:'hidden'}}>

                  <Box sx={{px:2.5,py:1.8,borderBottom:`1px solid ${IOS.separator}`,
                    display:'flex',alignItems:'center',justifyContent:'space-between',gap:1,flexWrap:'wrap'}}>
                    <Box>
                      <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label}}>
                        {rolSel.ROL_DESCRIPCION}
                      </Typography>
                      <Typography sx={{fontSize:'0.7rem',color:IOS.label2}}>
                        {permisosRol.length} permisos asignados
                      </Typography>
                    </Box>
                    <Box sx={{display:'flex',gap:1}}>
                      <Box component="button" onClick={abrirAsignar}
                        sx={{height:32,px:1.5,borderRadius:'10px',border:'none',bgcolor:IOS.green,cursor:'pointer',
                          display:'flex',alignItems:'center',gap:0.6,'&:hover':{filter:'brightness(0.9)'}}}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        <Typography sx={{fontSize:'0.78rem',fontWeight:700,color:'white'}}>Agregar permiso</Typography>
                      </Box>
                      <Box component="button" onClick={()=>setRolSel(null)}
                        sx={{width:32,height:32,borderRadius:'50%',border:`1px solid ${IOS.separator}`,bgcolor:IOS.bg,
                          cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label2} strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{p:2,maxHeight:500,overflowY:'auto',
                    '&::-webkit-scrollbar':{width:3},'&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4}}}>
                    {cargPermRol
                      ?<Box sx={{py:4,textAlign:'center'}}><CircularProgress size={20} sx={{color:IOS.purple}}/></Box>
                      :permisosRol.length===0
                        ?<Typography sx={{py:4,textAlign:'center',color:IOS.label2,fontSize:'0.8rem'}}>
                          Sin permisos — haz clic en "Agregar permiso"
                        </Typography>
                        :<Box sx={{display:'flex',flexDirection:'column',gap:1.5}}>
                          {Object.entries(permisosRolAgrupados).map(([modulo,items])=>(
                            <Box key={modulo}>
                              <Box sx={{display:'flex',alignItems:'center',gap:1,mb:0.7}}>
                                <Box sx={{px:0.8,py:0.2,borderRadius:'20px',
                                  bgcolor:modulo==='Principal'?IOS.bg:`${IOS.blue}10`,
                                  border:`1px solid ${modulo==='Principal'?IOS.separator:IOS.blue+'30'}`}}>
                                  <Typography sx={{fontSize:'0.58rem',fontWeight:700,letterSpacing:'0.05em',
                                    textTransform:'uppercase',
                                    color:modulo==='Principal'?IOS.label2:IOS.blue}}>{modulo}</Typography>
                                </Box>
                                <Box sx={{flex:1,height:'1px',bgcolor:IOS.separator}}/>
                                <Typography sx={{fontSize:'0.58rem',color:IOS.label3}}>{items.length}</Typography>
                              </Box>

                              <Box sx={{display:'flex',flexDirection:'column',gap:0.5}}>
                                {items.map((p,i)=>{
                                  const idP = p.PERM_IDPERMISO||p.RPERM_IDPERMISO;
                                  const esEditando = modificandoPriv===idP;
                                  const privActual = p.RPERM_PRIVILEGIO||'Solo lectura';
                                  return (
                                    <Box key={i} sx={{px:1.2,py:1,bgcolor:IOS.bg,borderRadius:'10px',
                                      border:`1px solid ${esEditando?IOS.orange:IOS.separator}`,
                                      transition:'all 0.15s'}}>
                                      {/* Fila principal */}
                                      <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:1}}>
                                        <Box sx={{flex:1,minWidth:0}}>
                                          <Typography sx={{fontSize:'0.75rem',color:IOS.label,fontWeight:600,fontFamily:'monospace'}}>
                                            {p.PERM_PERMISO_DESC||'—'}
                                          </Typography>
                                          {p.PERM_DEATALLE&&(
                                            <Typography sx={{fontSize:'0.67rem',color:IOS.label2,lineHeight:1.4,mt:0.2,
                                              overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                              {p.PERM_DEATALLE}
                                            </Typography>
                                          )}
                                        </Box>
                                        <Box sx={{display:'flex',alignItems:'center',gap:0.8,flexShrink:0}}>
                                          {/* Badge privilegio — clic para editar */}
                                          <Box onClick={()=>setModificandoPriv(esEditando?null:idP)}
                                            sx={{cursor:'pointer','&:hover':{opacity:0.8}}}>
                                            <BadgePriv priv={privActual}/>
                                          </Box>
                                          {/* Quitar */}
                                          <Tooltip title="Quitar permiso" placement="top">
                                            <Box onClick={()=>quitarPermiso(p)}
                                              sx={{width:22,height:22,borderRadius:'50%',bgcolor:'transparent',cursor:'pointer',
                                                display:'flex',alignItems:'center',justifyContent:'center',
                                                '&:hover':{bgcolor:'#FFF0EF'}}}>
                                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                            </Box>
                                          </Tooltip>
                                        </Box>
                                      </Box>

                                      {/* Selector privilegio expandible */}
                                      {esEditando&&(
                                        <Box sx={{mt:1,pt:1,borderTop:`1px dashed ${IOS.separator}`}}>
                                          <Typography sx={{fontSize:'0.58rem',fontWeight:700,color:IOS.label2,
                                            textTransform:'uppercase',letterSpacing:'0.07em',mb:0.6}}>
                                            Cambiar privilegio
                                          </Typography>
                                          <Box sx={{display:'flex',gap:0.6,flexWrap:'wrap'}}>
                                            {PRIVILEGIOS.map(priv=>(
                                              <Box key={priv} component="button"
                                                onClick={()=>modificarPrivilegio(p,priv)}
                                                disabled={guardPriv||priv===privActual}
                                                sx={{height:28,px:1.2,borderRadius:'8px',border:'none',cursor:priv===privActual?'default':'pointer',
                                                  bgcolor:priv===privActual?`${PRIV_COLOR[priv]}15`:`${PRIV_COLOR[priv]}08`,
                                                  outline:`1.5px solid ${priv===privActual?PRIV_COLOR[priv]:PRIV_COLOR[priv]+'40'}`,
                                                  display:'flex',alignItems:'center',gap:0.4,
                                                  opacity:guardPriv&&priv!==privActual?0.5:1,
                                                  transition:'all 0.15s'}}>
                                                {guardPriv&&<CircularProgress size={10} sx={{color:PRIV_COLOR[priv]}}/>}
                                                <Typography sx={{fontSize:'0.68rem',fontWeight:priv===privActual?700:500,
                                                  color:PRIV_COLOR[priv]}}>
                                                  {priv}{priv===privActual?' ✓':''}
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                    }
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ══ TAB PERMISOS ══ */}
          {tab==='permisos'&&(
            <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',border:`1px solid ${IOS.separator}`,
              boxShadow:'0 1px 4px rgba(0,0,0,0.07)',overflow:'hidden'}}>

              <Box sx={{px:2.5,py:1.8,borderBottom:`1px solid ${IOS.separator}`,
                display:'flex',alignItems:'center',gap:1.5,flexWrap:'wrap'}}>
                <Box sx={{position:'relative',flex:1,minWidth:140}}>
                  <Box sx={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:IOS.label2,pointerEvents:'none'}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                  </Box>
                  <input placeholder="Buscar permiso..." value={busqPermTab}
                    onChange={e=>setBusqPermTab(e.target.value)}
                    style={{width:'100%',height:32,paddingLeft:26,paddingRight:8,
                      border:`1px solid ${IOS.separator}`,borderRadius:8,fontSize:'0.78rem',
                      color:IOS.label,backgroundColor:IOS.bg,outline:'none',fontFamily:'inherit',boxSizing:'border-box'}}
                    onFocus={e=>e.target.style.borderColor=IOS.blue}
                    onBlur={e=>e.target.style.borderColor=IOS.separator}/>
                </Box>
                <Box sx={{px:1.2,py:0.3,bgcolor:IOS.bg,borderRadius:'20px',border:`1px solid ${IOS.separator}`}}>
                  <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.purple}}>{permisos.length} permisos</Typography>
                </Box>
                <Box component="button" onClick={()=>{setEditPerm(null);setFormPerm({desc:'',detalle:'',idModulo:null});setModalPerm(true);}}
                  sx={{height:32,px:1.5,borderRadius:'10px',border:'none',bgcolor:IOS.purple,cursor:'pointer',
                    display:'flex',alignItems:'center',gap:0.6,'&:hover':{filter:'brightness(0.9)'}}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  <Typography sx={{fontSize:'0.78rem',fontWeight:700,color:'white'}}>Nuevo permiso</Typography>
                </Box>
              </Box>

              {cargPerms
                ?<Box sx={{py:6,textAlign:'center'}}><CircularProgress size={24} sx={{color:IOS.purple}}/></Box>
                :<>
                  {/* Desktop */}
                  <Box sx={{display:{xs:'none',md:'block'},overflowX:'auto',
                    '&::-webkit-scrollbar':{height:3},'&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4}}}>
                    <table style={{width:'100%',borderCollapse:'collapse',minWidth:500}}>
                      <thead><tr>
                        <TH w={50} center>#</TH>
                        <TH>Nombre del permiso</TH>
                        <TH w={130}>Módulo</TH>
                        <TH center w={80}>Estado</TH>
                        <TH center w={80}>Acciones</TH>
                      </tr></thead>
                      <tbody>
                        {permisosFilt.length===0
                          ?<tr><td colSpan={5} style={{padding:'32px',textAlign:'center',color:IOS.label2,fontSize:'0.8rem'}}>Sin permisos</td></tr>
                          :permisosFilt.map((p,i)=>(
                            <tr key={i}
                              style={{borderBottom:`1px solid ${IOS.separator}`,backgroundColor:IOS.surface,transition:'background 0.1s'}}
                              onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                              onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                              <td style={{padding:'9px 10px',textAlign:'center',fontSize:'0.68rem',color:IOS.label2,fontFamily:'monospace'}}>{p.PERM_IDPERMISO}</td>
                              <td style={{padding:'9px 10px'}}>
                                <Box sx={{display:'flex',alignItems:'center',gap:1}}>
                                  <Box sx={{width:6,height:6,borderRadius:'50%',bgcolor:IOS.purple,flexShrink:0}}/>
                                  <Box>
                                    <Typography sx={{fontSize:'0.78rem',color:IOS.label,fontWeight:600,fontFamily:'monospace'}}>{p.PERM_PERMISO_DESC}</Typography>
                                    {p.PERM_DEATALLE&&<Typography sx={{fontSize:'0.68rem',color:IOS.label2,mt:0.2,lineHeight:1.4}}>{p.PERM_DEATALLE}</Typography>}
                                  </Box>
                                </Box>
                              </td>
                              <td style={{padding:'9px 10px'}}>
                                {p.MODULO
                                  ?<Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'20px',bgcolor:`${IOS.blue}10`,border:`1px solid ${IOS.blue}20`}}>
                                    <Typography sx={{fontSize:'0.62rem',fontWeight:600,color:IOS.blue,fontFamily:'monospace'}}>{p.MODULO==='MÓDULO PRINCIPAL'?'Principal':p.MODULO}</Typography>
                                  </Box>
                                  :<Typography sx={{fontSize:'0.62rem',color:IOS.label3}}>—</Typography>
                                }
                              </td>
                              <td style={{padding:'9px 10px',textAlign:'center'}}>
                                <Box sx={{display:'inline-flex',alignItems:'center',gap:0.5,px:0.8,py:0.2,borderRadius:'20px',
                                  bgcolor:p.PERM_ESTADO==='A'?'#EDFAF2':'#F2F2F7',
                                  border:`1px solid ${p.PERM_ESTADO==='A'?'#B7EFD0':IOS.separator}`}}>
                                  <Box sx={{width:4,height:4,borderRadius:'50%',bgcolor:p.PERM_ESTADO==='A'?IOS.green:IOS.label3}}/>
                                  <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:p.PERM_ESTADO==='A'?'#1A6B3C':IOS.label2}}>
                                    {p.PERM_ESTADO==='A'?'Activo':'Inactivo'}
                                  </Typography>
                                </Box>
                              </td>
                              <td style={{padding:'7px 10px',textAlign:'center'}}>
                                <Box sx={{display:'flex',gap:0.5,justifyContent:'center'}}>
                                  <Tooltip title="Editar" placement="top">
                                    <Box onClick={()=>{setEditPerm(p);setFormPerm({desc:p.PERM_PERMISO_DESC||'',detalle:p.PERM_DEATALLE||'',idModulo:p.PERM_IDMODULO||null});setModalPerm(true);}}
                                      sx={{width:26,height:26,borderRadius:'50%',bgcolor:`${IOS.blue}12`,display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer','&:hover':{bgcolor:`${IOS.blue}22`}}}>
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    </Box>
                                  </Tooltip>
                                  <Tooltip title="Desactivar" placement="top">
                                    <Box onClick={()=>{setElimPerm(p);setModalElimPerm(true);}}
                                      sx={{width:26,height:26,borderRadius:'50%',bgcolor:'#FFF0EF',display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer','&:hover':{bgcolor:'#FFD4D1'}}}>
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                                    </Box>
                                  </Tooltip>
                                </Box>
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </Box>

                  {/* Mobile cards permisos */}
                  <Box sx={{display:{xs:'flex',md:'none'},flexDirection:'column',p:1.5,gap:1}}>
                    {permisosFilt.length===0
                      ?<Typography sx={{textAlign:'center',color:IOS.label2,fontSize:'0.82rem',py:4}}>Sin permisos</Typography>
                      :permisosFilt.map((p,i)=>(
                        <Box key={i} sx={{p:1.5,bgcolor:IOS.bg,borderRadius:'12px',border:`1px solid ${IOS.separator}`}}>
                          <Box sx={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',mb:0.8}}>
                            <Box sx={{flex:1,minWidth:0,mr:1}}>
                              <Typography sx={{fontSize:'0.78rem',fontWeight:600,color:IOS.label,fontFamily:'monospace'}}>{p.PERM_PERMISO_DESC}</Typography>
                              {p.PERM_DEATALLE&&<Typography sx={{fontSize:'0.68rem',color:IOS.label2,mt:0.2,lineHeight:1.4}}>{p.PERM_DEATALLE}</Typography>}
                            </Box>
                            <Box sx={{display:'inline-flex',alignItems:'center',gap:0.5,px:0.8,py:0.2,borderRadius:'20px',
                              bgcolor:p.PERM_ESTADO==='A'?'#EDFAF2':'#F2F2F7',
                              border:`1px solid ${p.PERM_ESTADO==='A'?'#B7EFD0':IOS.separator}`,flexShrink:0}}>
                              <Box sx={{width:4,height:4,borderRadius:'50%',bgcolor:p.PERM_ESTADO==='A'?IOS.green:IOS.label3}}/>
                              <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:p.PERM_ESTADO==='A'?'#1A6B3C':IOS.label2}}>
                                {p.PERM_ESTADO==='A'?'Activo':'Inactivo'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{display:'flex',gap:0.8}}>
                            <Box component="button" onClick={()=>{setEditPerm(p);setFormPerm({desc:p.PERM_PERMISO_DESC||'',detalle:p.PERM_DEATALLE||'',idModulo:p.PERM_IDMODULO||null});setModalPerm(true);}}
                              sx={{flex:1,height:30,borderRadius:'8px',border:`1px solid ${IOS.blue}30`,bgcolor:`${IOS.blue}08`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.5}}>
                              <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.blue}}>Editar</Typography>
                            </Box>
                            <Box component="button" onClick={()=>{setElimPerm(p);setModalElimPerm(true);}}
                              sx={{flex:1,height:30,borderRadius:'8px',border:'1px solid #FFD4D1',bgcolor:'#FFF0EF',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.5}}>
                              <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.red}}>Desactivar</Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))
                    }
                  </Box>
                </>
              }
            </Box>
          )}
        </Box>
      </Box>

      {toast&&<Toast msg={toast.msg} tipo={toast.tipo} onClose={()=>setToast(null)}/>}

      {/* Modal Rol */}
      <Modal open={modalRol} onClose={()=>setModalRol(false)} w={420}>
        <ModalHeader title={editRol?'Editar Rol':'Nuevo Rol'} subtitle={editRol?.ROL_DESCRIPCION}
          onClose={()=>setModalRol(false)} color={IOS.blue}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}/>
        <Box sx={{p:2.5,display:'flex',flexDirection:'column',gap:1.5,overflowY:'auto'}}>
          <Box>
            <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.5}}>Nombre del rol</Typography>
            <input value={formRol.desc} onChange={e=>setFormRol(f=>({...f,desc:e.target.value}))}
              placeholder="Ej: JEFE DE FARMACIA"
              style={{width:'100%',height:36,padding:'0 10px',border:`1px solid ${IOS.separator}`,borderRadius:8,
                fontSize:'0.8rem',color:IOS.label,backgroundColor:IOS.surface,outline:'none',fontFamily:'inherit',boxSizing:'border-box'}}
              onFocus={e=>e.target.style.borderColor=IOS.blue}
              onBlur={e=>e.target.style.borderColor=IOS.separator}/>
          </Box>
          <Box>
            <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.8}}>Nivel de acceso</Typography>
            <Box sx={{display:'flex',flexWrap:'wrap',gap:0.8}}>
              {NIVELES.map(n=>(
                <Box key={n} onClick={()=>setFormRol(f=>({...f,nivel:n}))}
                  sx={{px:1.2,py:0.5,borderRadius:'8px',cursor:'pointer',transition:'all 0.15s',
                    border:`1.5px solid ${formRol.nivel===n?nivelColor(n):IOS.separator}`,
                    bgcolor:formRol.nivel===n?`${nivelColor(n)}08`:IOS.bg}}>
                  <Typography sx={{fontSize:'0.72rem',fontWeight:formRol.nivel===n?700:500,
                    color:formRol.nivel===n?nivelColor(n):IOS.label2}}>{n}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box>
            <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.8}}>Tipo de establecimiento</Typography>
            <Box sx={{display:'flex',gap:0.8}}>
              {[{v:'A',l:'Administrativo',c:IOS.orange},{v:'I',l:'IPRESS',c:IOS.purple},{v:'U',l:'Ejecutora',c:IOS.blue}].map(op=>(
                <Box key={op.v} onClick={()=>setFormRol(f=>({...f,tipo:op.v}))}
                  sx={{flex:1,py:0.8,borderRadius:'10px',cursor:'pointer',textAlign:'center',transition:'all 0.15s',
                    border:`1.5px solid ${formRol.tipo===op.v?op.c:IOS.separator}`,
                    bgcolor:formRol.tipo===op.v?`${op.c}08`:IOS.bg}}>
                  <Typography sx={{fontSize:{xs:'0.65rem',sm:'0.72rem'},fontWeight:formRol.tipo===op.v?700:500,
                    color:formRol.tipo===op.v?op.c:IOS.label2}}>{op.l}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{display:'flex',gap:1.5,mt:0.5}}>
            <Box component="button" onClick={()=>setModalRol(false)}
              sx={{flex:1,height:36,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',fontSize:'0.82rem',color:IOS.label2}}>
              Cancelar
            </Box>
            <Box component="button" onClick={guardarRol} disabled={!formRol.desc.trim()||guardRol}
              sx={{flex:1,height:36,borderRadius:'10px',border:'none',
                bgcolor:!formRol.desc.trim()||guardRol?'#E5E5EA':IOS.blue,
                cursor:!formRol.desc.trim()||guardRol?'default':'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:0.8}}>
              {guardRol&&<Spinner/>}
              <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:!formRol.desc.trim()||guardRol?'#8E8E93':'white'}}>
                {guardRol?'Guardando...':(editRol?'Actualizar':'Crear rol')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Modal eliminar rol */}
      <Modal open={modalElimRol&&!!elimRol} onClose={()=>setModalElimRol(false)} w={360}>
        <ModalHeader title="Desactivar rol" subtitle={elimRol?.ROL_DESCRIPCION}
          onClose={()=>setModalElimRol(false)} color={IOS.red}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}/>
        <Box sx={{p:2.5}}>
          <Box sx={{p:1.5,bgcolor:'#FFF0EF',borderRadius:'10px',border:'1px solid #FFD4D1',mb:2}}>
            <Typography sx={{fontSize:'0.76rem',color:'#C0392B'}}>
              ⚠ El rol <strong>{elimRol?.ROL_DESCRIPCION}</strong> será desactivado.
            </Typography>
          </Box>
          <Box sx={{display:'flex',gap:1.5}}>
            <Box component="button" onClick={()=>setModalElimRol(false)}
              sx={{flex:1,height:36,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',fontSize:'0.82rem',color:IOS.label2}}>
              Cancelar
            </Box>
            <Box component="button" onClick={eliminarRol} disabled={eliminando}
              sx={{flex:1,height:36,borderRadius:'10px',border:'none',bgcolor:eliminando?'#E5E5EA':IOS.red,
                cursor:eliminando?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.8}}>
              {eliminando&&<Spinner/>}
              <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:eliminando?'#8E8E93':'white'}}>
                {eliminando?'Desactivando...':'Sí, desactivar'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Modal asignar permiso */}
      <Modal open={modalAsignar} onClose={()=>setModalAsignar(false)} w={500}>
        <ModalHeader title="Asignar permiso" subtitle={rolSel?.ROL_DESCRIPCION}
          onClose={()=>setModalAsignar(false)} color={IOS.green}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>}/>

        {/* Selector privilegio por defecto */}
        <Box sx={{px:2.5,py:1.5,borderBottom:`1px solid ${IOS.separator}`,flexShrink:0}}>
          <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.8}}>
            Privilegio al asignar
          </Typography>
          <Box sx={{display:'flex',gap:0.8}}>
            {PRIVILEGIOS.map(priv=>(
              <Box key={priv} onClick={()=>setPrivSelAsignar(priv)}
                sx={{flex:1,py:0.6,borderRadius:'8px',cursor:'pointer',textAlign:'center',transition:'all 0.15s',
                  border:`1.5px solid ${privSelAsignar===priv?PRIV_COLOR[priv]:IOS.separator}`,
                  bgcolor:privSelAsignar===priv?`${PRIV_COLOR[priv]}10`:IOS.bg}}>
                <Typography sx={{fontSize:'0.68rem',fontWeight:privSelAsignar===priv?700:500,
                  color:privSelAsignar===priv?PRIV_COLOR[priv]:IOS.label2}}>{priv}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Búsqueda */}
        <Box sx={{px:2.5,py:1.2,borderBottom:`1px solid ${IOS.separator}`,flexShrink:0}}>
          <Box sx={{position:'relative'}}>
            <Box sx={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:IOS.label2,pointerEvents:'none'}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            </Box>
            <input placeholder="Buscar permiso disponible..." value={busqPerm}
              onChange={e=>setBusqPerm(e.target.value)}
              style={{width:'100%',height:34,paddingLeft:26,paddingRight:10,
                border:`1px solid ${IOS.separator}`,borderRadius:8,fontSize:'0.78rem',
                color:IOS.label,backgroundColor:IOS.bg,outline:'none',fontFamily:'inherit',boxSizing:'border-box'}}
              onFocus={e=>e.target.style.borderColor=IOS.green}
              onBlur={e=>e.target.style.borderColor=IOS.separator}/>
          </Box>
        </Box>

        <Box sx={{flex:1,overflowY:'auto',p:1.5,maxHeight:360,
          '&::-webkit-scrollbar':{width:3},'&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4}}}>
          {Object.keys(permDisp).length===0
            ?<Typography sx={{p:2,textAlign:'center',color:IOS.label2,fontSize:'0.8rem'}}>
              {busqPerm?'Sin coincidencias':'Todos los permisos ya están asignados'}
            </Typography>
            :Object.entries(permDisp).map(([modulo,items])=>(
              <Box key={modulo} sx={{mb:1.5}}>
                <Box sx={{display:'flex',alignItems:'center',gap:1,mb:0.8,px:0.5}}>
                  <Box sx={{px:0.8,py:0.2,borderRadius:'20px',
                    bgcolor:modulo==='MÓDULO PRINCIPAL'?IOS.bg:`${IOS.blue}10`,
                    border:`1px solid ${modulo==='MÓDULO PRINCIPAL'?IOS.separator:IOS.blue+'30'}`}}>
                    <Typography sx={{fontSize:'0.58rem',fontWeight:700,
                      color:modulo==='MÓDULO PRINCIPAL'?IOS.label2:IOS.blue,
                      textTransform:'uppercase',letterSpacing:'0.05em'}}>
                      {modulo==='MÓDULO PRINCIPAL'?'Principal':modulo}
                    </Typography>
                  </Box>
                  <Box sx={{flex:1,height:'1px',bgcolor:IOS.separator}}/>
                  <Typography sx={{fontSize:'0.58rem',color:IOS.label3}}>{items.length}</Typography>
                </Box>
                <Box sx={{display:'flex',flexDirection:'column',gap:0.5}}>
                  {items.map((p,i)=>(
                    <Box key={i} onClick={()=>asignarPermiso(p)}
                      sx={{display:'flex',alignItems:'center',justifyContent:'space-between',
                        px:1.2,py:0.8,bgcolor:IOS.bg,borderRadius:'10px',border:`1px solid ${IOS.separator}`,
                        cursor:'pointer',transition:'all 0.15s',
                        '&:hover':{border:`1px solid ${IOS.green}40`,bgcolor:`${IOS.green}04`}}}>
                      <Box sx={{flex:1,minWidth:0,mr:1}}>
                        <Typography sx={{fontSize:'0.75rem',color:IOS.label,fontWeight:600,fontFamily:'monospace'}}>
                          {p.PERM_PERMISO_DESC}
                        </Typography>
                        {p.PERM_DEATALLE&&(
                          <Typography sx={{fontSize:'0.68rem',color:IOS.label2,lineHeight:1.4,
                            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                            {p.PERM_DEATALLE}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{display:'flex',alignItems:'center',gap:0.8,flexShrink:0}}>
                        <BadgePriv priv={privSelAsignar}/>
                        <Box sx={{width:22,height:22,borderRadius:'50%',bgcolor:`${IOS.green}12`,
                          display:'flex',alignItems:'center',justifyContent:'center'}}>
                          {asignando
                            ?<CircularProgress size={10} sx={{color:IOS.green}}/>
                            :<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                          }
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))
          }
        </Box>
      </Modal>

      {/* Modal permiso crear/editar */}
      <Modal open={modalPerm} onClose={()=>setModalPerm(false)} w={400}>
        <ModalHeader title={editPerm?'Editar Permiso':'Nuevo Permiso'}
          subtitle={editPerm?.PERM_PERMISO_DESC} onClose={()=>setModalPerm(false)} color={IOS.purple}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>}/>
        <Box sx={{p:2.5,display:'flex',flexDirection:'column',gap:1.5,overflowY:'auto'}}>
          <Box>
            <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.5}}>Código del permiso</Typography>
            <input value={formPerm.desc}
              onChange={e=>setFormPerm(f=>({...f,desc:e.target.value.toLowerCase()}))}
              placeholder="ej: gestionar-usuarios"
              style={{width:'100%',height:36,padding:'0 10px',border:`1px solid ${IOS.separator}`,borderRadius:8,
                fontSize:'0.82rem',color:IOS.label,backgroundColor:IOS.surface,outline:'none',
                fontFamily:'monospace',boxSizing:'border-box'}}
              onFocus={e=>e.target.style.borderColor=IOS.purple}
              onBlur={e=>e.target.style.borderColor=IOS.separator}/>
            <Typography sx={{fontSize:'0.58rem',color:IOS.label2,mt:0.4}}>En minúsculas — identificador interno del permiso.</Typography>
          </Box>
          <Box>
            <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.5}}>Descripción legible</Typography>
            <textarea value={formPerm.detalle}
              onChange={e=>setFormPerm(f=>({...f,detalle:e.target.value}))}
              placeholder="Permite gestionar..."
              rows={3}
              style={{width:'100%',padding:'8px 10px',border:`1px solid ${IOS.separator}`,borderRadius:8,
                fontSize:'0.8rem',color:IOS.label,backgroundColor:IOS.surface,outline:'none',
                fontFamily:'inherit',boxSizing:'border-box',resize:'vertical',lineHeight:1.6}}
              onFocus={e=>e.target.style.borderColor=IOS.purple}
              onBlur={e=>e.target.style.borderColor=IOS.separator}/>
          </Box>
          <Box>
            <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.8}}>Módulo padre (opcional)</Typography>
            <Box sx={{display:'flex',flexDirection:'column',gap:0.6}}>
              <Box onClick={()=>setFormPerm(f=>({...f,idModulo:null}))}
                sx={{px:1.2,py:0.8,borderRadius:'10px',cursor:'pointer',
                  border:`1.5px solid ${!formPerm.idModulo?IOS.blue:IOS.separator}`,
                  bgcolor:!formPerm.idModulo?`${IOS.blue}06`:IOS.bg,
                  display:'flex',alignItems:'center',gap:1}}>
                <Box sx={{width:6,height:6,borderRadius:'50%',bgcolor:!formPerm.idModulo?IOS.blue:IOS.label3,flexShrink:0}}/>
                <Typography sx={{fontSize:'0.75rem',fontWeight:!formPerm.idModulo?700:400,
                  color:!formPerm.idModulo?IOS.blue:IOS.label2}}>Módulo principal (sin padre)</Typography>
              </Box>
              <Box sx={{maxHeight:120,overflowY:'auto',display:'flex',flexDirection:'column',gap:0.4,
                '&::-webkit-scrollbar':{width:3},'&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4}}}>
                {modulosPadre.map(m=>(
                  <Box key={m.PERM_IDPERMISO} onClick={()=>setFormPerm(f=>({...f,idModulo:m.PERM_IDPERMISO}))}
                    sx={{px:1.2,py:0.7,borderRadius:'8px',cursor:'pointer',
                      border:`1.5px solid ${formPerm.idModulo===m.PERM_IDPERMISO?IOS.purple:IOS.separator}`,
                      bgcolor:formPerm.idModulo===m.PERM_IDPERMISO?`${IOS.purple}06`:IOS.bg,
                      display:'flex',alignItems:'center',gap:1}}>
                    <Box sx={{width:6,height:6,borderRadius:'50%',
                      bgcolor:formPerm.idModulo===m.PERM_IDPERMISO?IOS.purple:IOS.label3,flexShrink:0}}/>
                    <Typography sx={{fontSize:'0.75rem',fontFamily:'monospace',
                      fontWeight:formPerm.idModulo===m.PERM_IDPERMISO?700:400,
                      color:formPerm.idModulo===m.PERM_IDPERMISO?IOS.purple:IOS.label}}>
                      {m.PERM_PERMISO_DESC}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box sx={{display:'flex',gap:1.5}}>
            <Box component="button" onClick={()=>setModalPerm(false)}
              sx={{flex:1,height:36,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',fontSize:'0.82rem',color:IOS.label2}}>
              Cancelar
            </Box>
            <Box component="button" onClick={guardarPerm} disabled={!formPerm.desc.trim()||guardPerm}
              sx={{flex:1,height:36,borderRadius:'10px',border:'none',
                bgcolor:!formPerm.desc.trim()||guardPerm?'#E5E5EA':IOS.purple,
                cursor:!formPerm.desc.trim()||guardPerm?'default':'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:0.8}}>
              {guardPerm&&<Spinner/>}
              <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:!formPerm.desc.trim()||guardPerm?'#8E8E93':'white'}}>
                {guardPerm?'Guardando...':(editPerm?'Actualizar':'Crear')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Modal eliminar permiso */}
      <Modal open={modalElimPerm&&!!elimPerm} onClose={()=>setModalElimPerm(false)} w={360}>
        <ModalHeader title="Desactivar permiso" subtitle={elimPerm?.PERM_PERMISO_DESC}
          onClose={()=>setModalElimPerm(false)} color={IOS.red}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}/>
        <Box sx={{p:2.5}}>
          <Box sx={{p:1.5,bgcolor:'#FFF0EF',borderRadius:'10px',border:'1px solid #FFD4D1',mb:2}}>
            <Typography sx={{fontSize:'0.76rem',color:'#C0392B'}}>
              ⚠ El permiso <strong>{elimPerm?.PERM_PERMISO_DESC}</strong> será desactivado.
            </Typography>
          </Box>
          <Box sx={{display:'flex',gap:1.5}}>
            <Box component="button" onClick={()=>setModalElimPerm(false)}
              sx={{flex:1,height:36,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',fontSize:'0.82rem',color:IOS.label2}}>
              Cancelar
            </Box>
            <Box component="button" onClick={eliminarPerm} disabled={eliminandoP}
              sx={{flex:1,height:36,borderRadius:'10px',border:'none',bgcolor:eliminandoP?'#E5E5EA':IOS.red,
                cursor:eliminandoP?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.8}}>
              {eliminandoP&&<Spinner/>}
              <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:eliminandoP?'#8E8E93':'white'}}>
                {eliminandoP?'Desactivando...':'Sí, desactivar'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default Roles;
